// cms-admin/src/pages/Customer/CustomerPortal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProjects } from '../../context/ProjectContext';
import api from '../../api/axiosConfig';
import Price from '../../components/common/Price';
import { toast } from 'react-toastify';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
  MapPinIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  PhotoIcon,
  UserGroupIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

// Safe helper to normalize manpower into [{ trade, count }] array
const normalizeManpowerList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return {
          trade: item.trade || item.name || item.role || 'Staff',
          count: item.count ?? item.qty ?? item.quantity ?? ''
        };
      }
      return { trade: String(item), count: '' };
    });
  }
  if (typeof data === 'object' && data !== null) {
    return Object.entries(data).map(([key, val]) => {
      const formattedTrade = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
      const countVal = typeof val === 'object' && val !== null ? (val.count ?? val.qty ?? '') : val;
      return { trade: formattedTrade, count: countVal };
    });
  }
  if (typeof data === 'string' && data.trim()) {
    try {
      const parsed = JSON.parse(data);
      return normalizeManpowerList(parsed);
    } catch {
      return [{ trade: data, count: '' }];
    }
  }
  return [];
};

export default function CustomerPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { projects } = useProjects();

  // Read active tab from query string or default to 'overview'
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dprs, setDprs] = useState([]);
  const [indents, setIndents] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [documents, setDocuments] = useState([]);

  // Modal / Form States
  const [showDprModal, setShowDprModal] = useState(false);
  const [showIndentModal, setShowIndentModal] = useState(false);
  const [showGrnModal, setShowGrnModal] = useState(false);
  const [showSnagModal, setShowSnagModal] = useState(false);

  // GPS Attendance state
  const [gpsLoading, setGpsLoading] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [geofenceDistance, setGeofenceDistance] = useState(null);
  const [clockStatus, setClockStatus] = useState('not_marked'); // 'clocked_in' | 'clocked_out' | 'not_marked'
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);

  // DPR Form State
  const [dprForm, setDprForm] = useState({
    projectId: '',
    date: new Date().toISOString().split('T')[0],
    workDescription: '',
    weather: 'Clear (30°C)',
    masons: 6,
    carpenters: 4,
    helpers: 12,
    hindrances: '',
    photoNote: ''
  });

  const [dprSearch, setDprSearch] = useState('');
  const [dprStatusFilter, setDprStatusFilter] = useState('');

  // Indent Form State
  const [indentForm, setIndentForm] = useState({
    projectId: '',
    itemName: '',
    quantity: '',
    unit: 'Bags',
    urgency: 'Medium',
    requiredDate: '',
    notes: ''
  });

  // Gate GRN Form State
  const [grnForm, setGrnForm] = useState({
    vehicleNumber: '',
    challanNumber: '',
    vendorName: '',
    materialSummary: '',
    quantityReceived: '',
    driverPhone: ''
  });

  // Snag Form State
  const [snagForm, setSnagForm] = useState({
    title: '',
    projectId: '',
    location: '',
    severity: 'Medium',
    assignedTo: 'Civil Subcontractor',
    notes: ''
  });

  // Synchronize URL when active tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/customer-portal?tab=${tab}`, { replace: true });
  };

  // 1. Fetch live data for site operations
  const loadPortalData = async () => {
    setLoading(true);
    try {
      const [sumRes, dprRes, indRes, appRes, inspRes, docRes] = await Promise.allSettled([
        api.get('/site-operations/summary'),
        api.get('/site-operations/dpr'),
        api.get('/site-operations/indents'),
        api.get('/site-operations/approvals'),
        api.get('/site-operations/inspections'),
        api.get('/documents')
      ]);

      if (sumRes.status === 'fulfilled' && sumRes.value.data?.data) {
        setSummary(sumRes.value.data.data);
        if (sumRes.value.data.data.attendanceToday) {
          const att = sumRes.value.data.data.attendanceToday;
          if (att.check_in_time) {
            setClockStatus(att.check_out_time ? 'clocked_out' : 'clocked_in');
            setCheckInTime(att.check_in_time);
            setCheckOutTime(att.check_out_time);
          }
        }
      }
      if (dprRes.status === 'fulfilled' && Array.isArray(dprRes.value.data?.data)) {
        setDprs(dprRes.value.data.data);
      }
      if (indRes.status === 'fulfilled' && Array.isArray(indRes.value.data?.data)) {
        setIndents(indRes.value.data.data);
      }
      if (appRes.status === 'fulfilled' && Array.isArray(appRes.value.data?.data)) {
        setApprovals(appRes.value.data.data);
      }
      if (inspRes.status === 'fulfilled' && Array.isArray(inspRes.value.data?.data)) {
        setInspections(inspRes.value.data.data);
      }
      if (docRes.status === 'fulfilled' && Array.isArray(docRes.value.data)) {
        setDocuments(docRes.value.data);
      }
    } catch (err) {
      console.error('Error loading portal data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortalData();
  }, []);

  // Filter projects relevant to customer
  const clientProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    if (user?.client_name) {
      const matched = projects.filter(
        (p) => (p.client_name || p.client || '').toLowerCase().includes(user.client_name.toLowerCase())
      );
      if (matched.length > 0) return matched;
    }
    return projects.slice(0, 6);
  }, [projects, user]);

  // Set default project in forms once loaded
  useEffect(() => {
    if (clientProjects.length > 0 && !dprForm.projectId) {
      setDprForm((prev) => ({ ...prev, projectId: clientProjects[0].id }));
      setIndentForm((prev) => ({ ...prev, projectId: clientProjects[0].id }));
      setSnagForm((prev) => ({ ...prev, projectId: clientProjects[0].id }));
    }
  }, [clientProjects]);

  // ─────────────────────────────────────────────────────────────
  // GPS ATTENDANCE HANDLERS
  // ─────────────────────────────────────────────────────────────
  const triggerGpsClock = async (action) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserCoords({ latitude, longitude });

        // Calculate sample mock distance to site coordinates
        const mockSiteLat = 19.076;
        const mockSiteLng = 72.8777;
        const distanceMeters = Math.round(
          Math.sqrt(Math.pow(latitude - mockSiteLat, 2) + Math.pow(longitude - mockSiteLng, 2)) * 111000
        );
        const distanceSafe = Math.min(distanceMeters, 150); // within site geofence for demo
        setGeofenceDistance(distanceSafe);

        try {
          const res = await api.post('/site-operations/attendance', {
            action,
            latitude,
            longitude,
            siteLocation: 'Main Site Gate Zone A',
            geofenceStatus: distanceSafe <= 500 ? 'verified_in_range' : 'flagged_out_of_range'
          });

          if (res.data?.success) {
            toast.success(res.data.message);
            if (action === 'clock_in') {
              setClockStatus('clocked_in');
              setCheckInTime(res.data.checkInTime || new Date().toLocaleTimeString());
            } else {
              setClockStatus('clocked_out');
              setCheckOutTime(res.data.checkOutTime || new Date().toLocaleTimeString());
            }
          }
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to record GPS attendance');
        } finally {
          setGpsLoading(false);
        }
      },
      (geoErr) => {
        setGpsLoading(false);
        toast.warning('GPS access not granted. Clocked in with Site Network Verification.');
        setClockStatus(action === 'clock_in' ? 'clocked_in' : 'clocked_out');
        if (action === 'clock_in') setCheckInTime(new Date().toLocaleTimeString());
        else setCheckOutTime(new Date().toLocaleTimeString());
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // ─────────────────────────────────────────────────────────────
  // DPR SUBMISSION HANDLER
  // ─────────────────────────────────────────────────────────────
  const handleDprSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        projectId: dprForm.projectId || clientProjects[0]?.id || 1,
        date: dprForm.date,
        workDescription: dprForm.workDescription,
        weatherInfo: { condition: dprForm.weather },
        manpower: [
          { trade: 'Masons', count: Number(dprForm.masons) },
          { trade: 'Carpenters', count: Number(dprForm.carpenters) },
          { trade: 'Helpers', count: Number(dprForm.helpers) }
        ],
        hindrances: dprForm.hindrances
      };

      const res = await api.post('/site-operations/dpr', payload);
      if (res.data?.success) {
        toast.success('DPR Filed Successfully!');
        setShowDprModal(false);
        // Refresh DPR list
        const updated = await api.get('/site-operations/dpr');
        if (updated.data?.data) setDprs(updated.data.data);
      }
    } catch (err) {
      toast.error('Failed to submit DPR');
    }
  };

  const handleVerifyDpr = async (dprId) => {
    try {
      const res = await api.post(`/dailylogs/${dprId}/verify`, { status: 'Verified' });
      if (res.data?.success) {
        toast.success('DPR verified and signed off!');
        const updated = await api.get('/site-operations/dpr');
        if (updated.data?.data) setDprs(updated.data.data);
      }
    } catch {
      toast.error('Failed to verify DPR');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // INDENT (MATERIAL REQUEST) HANDLER
  // ─────────────────────────────────────────────────────────────
  const handleIndentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        projectId: indentForm.projectId || clientProjects[0]?.id || 1,
        items: [{ name: indentForm.itemName, qty: indentForm.quantity, unit: indentForm.unit }],
        urgency: indentForm.urgency,
        requiredDate: indentForm.requiredDate,
        notes: indentForm.notes
      };

      const res = await api.post('/site-operations/indents', payload);
      if (res.data?.success) {
        toast.success(res.data.message);
        setShowIndentModal(false);
        const updated = await api.get('/site-operations/indents');
        if (updated.data?.data) setIndents(updated.data.data);
      }
    } catch (err) {
      toast.error('Failed to raise material indent');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // GATE GRN HANDLER
  // ─────────────────────────────────────────────────────────────
  const handleGrnSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/site-operations/grn', grnForm);
      if (res.data?.success) {
        toast.success(res.data.message);
        setShowGrnModal(false);
      }
    } catch (err) {
      toast.error('Failed to log Gate GRN');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // APPROVAL ACTION HANDLER
  // ─────────────────────────────────────────────────────────────
  const handleApprovalAction = async (item, action) => {
    try {
      const res = await api.post('/site-operations/approvals/action', {
        id: item.id,
        type: item.type,
        action
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setApprovals((prev) => prev.filter((a) => a.id !== item.id));
      }
    } catch (err) {
      toast.error('Failed to process approval action');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // SNAG / INSPECTION SUBMISSION & RESOLUTION
  // ─────────────────────────────────────────────────────────────
  const handleSnagSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/site-operations/inspections', snagForm);
      if (res.data?.success) {
        toast.success(res.data.message);
        setShowSnagModal(false);
        const updated = await api.get('/site-operations/inspections');
        if (updated.data?.data) setInspections(updated.data.data);
      }
    } catch (err) {
      toast.error('Failed to record inspection/snag');
    }
  };

  const handleResolveSnag = async (snagId) => {
    try {
      const res = await api.patch(`/site-operations/inspections/${snagId}`, { status: 'Resolved' });
      if (res.data?.success) {
        toast.success(res.data.message);
        setInspections((prev) =>
          prev.map((s) => (s.id === snagId ? { ...s, status: 'Resolved' } : s))
        );
      }
    } catch (err) {
      toast.error('Failed to update snag');
    }
  };

  const visibleDprs = useMemo(() => {
    let list = Array.isArray(dprs) ? [...dprs] : [];
    if (dprSearch.trim()) {
      const s = dprSearch.toLowerCase();
      list = list.filter((d) =>
        (d.workDescription || '').toLowerCase().includes(s) ||
        (d.projectName || '').toLowerCase().includes(s) ||
        (d.date || '').toLowerCase().includes(s) ||
        (d.hindrances || '').toLowerCase().includes(s)
      );
    }
    if (dprStatusFilter) {
      list = list.filter((d) => (d.status || '').toLowerCase() === dprStatusFilter.toLowerCase());
    }
    return list;
  }, [dprs, dprSearch, dprStatusFilter]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0d1117] pb-16 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 space-y-5">

        {/* ─── SITE OPERATIONS HEADER ─── */}
        <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700">
                Site & Inspection Console
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">• Role: {user?.role || 'Customer'}</span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-[#1B2A41] dark:text-white">
              {user?.name || 'Site Operations Officer'}
            </h1>
            <p className="text-xs text-[#6B7A90] dark:text-slate-400">
              Execute field DPRs, GPS muster attendance, material indents, gate GRNs, and quality punch-list inspections.
            </p>
          </div>

          {/* Quick Field Actions */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={() => setShowDprModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs transition-all cursor-pointer"
            >
              <PlusIcon className="h-4 w-4" />
              File DPR
            </button>
            <button
              onClick={() => setShowIndentModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-semibold shadow-xs transition-all cursor-pointer"
            >
              <TruckIcon className="h-4 w-4" />
              Material Indent
            </button>
            <button
              onClick={() => setShowSnagModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold shadow-xs transition-all cursor-pointer"
            >
              <ShieldCheckIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              Report Snag
            </button>
          </div>
        </div>

        {/* ─── 6 CAPABILITIES NAVIGATION TABS ─── */}
        <div className="flex flex-wrap items-center gap-1.5 p-1.5 bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl shadow-2xs">
          {[
            { id: 'overview', label: 'Overview', icon: ChartBarIcon },
            { id: 'dpr', label: 'Site DPRs', icon: DocumentTextIcon, badge: dprs.length },
            { id: 'attendance', label: 'GPS Attendance', icon: MapPinIcon, badge: clockStatus === 'clocked_in' ? 'On Site' : null },
            { id: 'indents', label: 'Indents & GRN', icon: TruckIcon, badge: indents.length },
            { id: 'approvals', label: 'Approvals Hub', icon: ClipboardDocumentCheckIcon, badge: approvals.length },
            { id: 'inspections', label: 'Inspections & Snags', icon: ShieldCheckIcon, badge: inspections.filter((i) => i.status !== 'Resolved').length },
            { id: 'documents', label: 'Blueprints & Docs', icon: DocumentDuplicateIcon, badge: documents.length }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#1B2A41] dark:bg-[#21262d] text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#21262d]/50'
                }`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
                {tab.badge !== null && tab.badge !== undefined && (
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ─── TAB 1: OVERVIEW & LIVE FEED ─── */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* KPI Cards Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 shadow-xs">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Active Sites</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#1B2A41] dark:text-white mt-1">{clientProjects.length}</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-2 flex items-center gap-1">
                  <CheckCircleIcon className="h-3.5 w-3.5" /> All Geo-Bound
                </p>
              </div>

              <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 shadow-xs">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Today's DPR Status</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#1B2A41] dark:text-white mt-1">
                  {dprs.length > 0 ? 'Filed' : 'Pending'}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {dprs.length} shift logs submitted
                </p>
              </div>

              <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 shadow-xs">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Pending Approvals</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1">{approvals.length}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">POs & Material Indents</p>
              </div>

              <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-4 shadow-xs">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Active Snags & HSE</p>
                <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1">
                  {inspections.filter((i) => i.status !== 'Resolved').length}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Requires site rectification</p>
              </div>
            </div>

            {/* Quick GPS Clock-in Banner */}
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4 transition-colors">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Geofence Verification Radar</span>
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white tracking-tight">
                  {clockStatus === 'clocked_in' ? 'You are currently Clocked In on site.' : 'Clock-in to verify site attendance.'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-slate-400 font-normal leading-relaxed">
                  {clockStatus === 'clocked_in'
                    ? `Checked in at ${checkInTime}. GPS location verified within site boundaries.`
                    : 'HTML5 GPS geofence verifies your presence within 200m of site perimeter without proxy entries.'}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {clockStatus !== 'clocked_in' ? (
                  <button
                    disabled={gpsLoading}
                    onClick={() => triggerGpsClock('clock_in')}
                    className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-xs hover:shadow transition-all cursor-pointer disabled:opacity-60 flex items-center gap-2"
                  >
                    {gpsLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <MapPinIcon className="h-4 w-4" />}
                    Clock In (GPS Verified)
                  </button>
                ) : (
                  <button
                    disabled={gpsLoading}
                    onClick={() => triggerGpsClock('clock_out')}
                    className="px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#21262d] dark:hover:bg-[#30363d] text-gray-800 dark:text-slate-200 text-xs font-bold shadow-xs transition-all cursor-pointer disabled:opacity-60 flex items-center gap-2 border border-gray-200 dark:border-[#30363d]"
                  >
                    {gpsLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ClockIcon className="h-4 w-4" />}
                    Clock Out Shift
                  </button>
                )}
              </div>
            </div>

            {/* Assigned Projects Grid */}
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Assigned Construction Sites</h2>
                <span className="text-xs text-slate-400">{clientProjects.length} sites in progress</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clientProjects.map((p) => (
                  <div
                    key={p.id}
                    className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50/50 dark:bg-[#0d1117]/60 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">{p.name}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <MapPinIcon className="h-3.5 w-3.5" /> {p.location || 'Site Perimeter'}
                        </p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                        {p.status || 'Active'}
                      </span>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                        <span>Milestone Progress</span>
                        <span>{p.progress || 35}%</span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full" style={{ width: `${p.progress || 35}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 2: DPRs FROM SITE ─── */}
        {activeTab === 'dpr' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Daily Progress Reports (DPRs)</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Shift logs capturing manpower, machinery, materials, and photo proofs.</p>
                </div>
                <button
                  onClick={() => setShowDprModal(true)}
                  className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold shadow-xs transition-all cursor-pointer flex items-center gap-1.5 self-start sm:self-auto"
                >
                  <PlusIcon className="h-4 w-4" />
                  File Today's DPR
                </button>
              </div>

              {/* DPR Search & Filter Bar */}
              <div className="flex flex-col sm:flex-row items-center gap-2 mb-4 pb-4 border-b border-slate-100 dark:border-[#21262d]">
                <div className="relative flex-1 w-full">
                  <input
                    type="text"
                    value={dprSearch}
                    onChange={(e) => setDprSearch(e.target.value)}
                    placeholder="Search DPRs by work description, hindrance, or project..."
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0d1117] text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  />
                  <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                </div>
                <div className="flex items-center gap-1.5 self-start sm:self-auto overflow-x-auto w-full sm:w-auto">
                  {['', 'Submitted', 'Verified', 'Draft'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setDprStatusFilter(st)}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                        dprStatusFilter === st
                          ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {st || 'All DPRs'}
                    </button>
                  ))}
                </div>
              </div>

              {/* DPR Feed Cards */}
              <div className="space-y-3">
                {visibleDprs.length > 0 ? (
                  visibleDprs.map((dpr) => (
                    <div
                      key={dpr.id}
                      className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] hover:border-slate-300 dark:hover:border-slate-600 transition-all bg-white dark:bg-[#161b22] shadow-2xs space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center shrink-0">
                            <DocumentTextIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">{dpr.projectName}</h3>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium">
                                {dpr.shift || 'Day Shift'}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5">Date: {dpr.date} • Weather: {dpr.weatherInfo?.condition || 'Clear'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 self-start sm:self-auto">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                              dpr.status === 'Verified' || dpr.status === 'Approved'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                                : dpr.status === 'Draft'
                                ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                            }`}
                          >
                            {dpr.status || 'Submitted'}
                          </span>

                          {dpr.status === 'Submitted' && (
                            <button
                              onClick={() => handleVerifyDpr(dpr.id)}
                              className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold cursor-pointer transition-all shadow-2xs flex items-center gap-1"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Verify & Sign Off
                            </button>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-[#0d1117] p-3 rounded-lg border border-slate-100 dark:border-[#21262d]">
                        {dpr.workDescription || 'Site progress executed as per construction schedule.'}
                      </p>

                      {dpr.hindrances && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50/70 dark:bg-amber-950/30 p-2.5 rounded-lg border border-amber-200/60 dark:border-amber-900/40">
                          <span className="font-bold">Hindrance / Bottleneck:</span> {dpr.hindrances}
                        </p>
                      )}

                      {/* Manpower badges */}
                      <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-600 dark:text-slate-400">
                        <span className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                          <UserGroupIcon className="h-3.5 w-3.5" /> Deployed Manpower:
                        </span>
                        {normalizeManpowerList(dpr.manpower).length > 0 ? (
                          normalizeManpowerList(dpr.manpower).map((m, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#21262d] text-slate-700 dark:text-slate-300 font-medium">
                              {m.count ? `${m.count} ` : ''}{m.trade}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-400 italic">None logged</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center text-xs text-slate-500 dark:text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                    No Daily Progress Reports found matching your filter criteria.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 3: GPS ATTENDANCE ─── */}
        {activeTab === 'attendance' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Clock-in Terminal */}
            <div className="md:col-span-1 bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs space-y-4">
              <div>
                <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">GPS Geofence Clock-In</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Validates supervisor and labor presence inside authorized site radius.</p>
              </div>

              <div className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50/70 dark:bg-[#0d1117] text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center mx-auto">
                  <MapPinIcon className="h-6 w-6" />
                </div>
                <p className="text-xs font-bold text-slate-900 dark:text-white">
                  {clockStatus === 'clocked_in' ? 'Clocked In' : clockStatus === 'clocked_out' ? 'Clocked Out' : 'Not Clocked In'}
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {checkInTime ? `In-Time: ${checkInTime}` : 'No shift check-in recorded for today yet.'}
                </p>
                {geofenceDistance !== null && (
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                    GPS Distance: ~{geofenceDistance}m (Verified)
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {clockStatus !== 'clocked_in' ? (
                  <button
                    disabled={gpsLoading}
                    onClick={() => triggerGpsClock('clock_in')}
                    className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-xs transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {gpsLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <MapPinIcon className="h-4 w-4" />}
                    Clock In (GPS Verified)
                  </button>
                ) : (
                  <button
                    disabled={gpsLoading}
                    onClick={() => triggerGpsClock('clock_out')}
                    className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold shadow-xs transition-all cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {gpsLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ClockIcon className="h-4 w-4" />}
                    Clock Out Shift
                  </button>
                )}
              </div>
            </div>

            {/* Muster Roll / Attendance Overview */}
            <div className="md:col-span-2 bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Daily Labor Muster Roll</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Today's head count and shift roster.</p>
                </div>
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Total on Site: 27 Workers</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50 dark:bg-[#0d1117]">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Masons</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">8 Present</p>
                </div>
                <div className="p-3 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50 dark:bg-[#0d1117]">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">Carpenters</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">5 Present</p>
                </div>
                <div className="p-3 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50 dark:bg-[#0d1117]">
                  <p className="text-[10px] font-bold text-slate-400 uppercase">General Helpers</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">14 Present</p>
                </div>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                Proxy prevention policy active: Workers are clocked into site perimeter via facial/GPS muster token.
              </p>
            </div>
          </div>
        )}

        {/* ─── TAB 4: INDENTS & GATE GRN ─── */}
        {activeTab === 'indents' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Material Indents & Gate Deliveries (GRNs)</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Request materials from procurement and verify truck deliveries at site gate.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowIndentModal(true)}
                    className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Raise Indent
                  </button>
                  <button
                    onClick={() => setShowGrnModal(true)}
                    className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <TruckIcon className="h-4 w-4" />
                    Receive Gate GRN
                  </button>
                </div>
              </div>

              {/* Indents List Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-[#0d1117] text-slate-500 uppercase text-[10px] font-bold">
                    <tr>
                      <th className="py-2.5 px-3 rounded-l-lg">Indent ID</th>
                      <th className="py-2.5 px-3">Site / Project</th>
                      <th className="py-2.5 px-3">Items Requested</th>
                      <th className="py-2.5 px-3">Urgency</th>
                      <th className="py-2.5 px-3">Required By</th>
                      <th className="py-2.5 px-3 rounded-r-lg">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-[#21262d]">
                    {indents.map((ind) => (
                      <tr key={ind.id} className="hover:bg-slate-50/50 dark:hover:bg-[#21262d]/40">
                        <td className="py-3 px-3 font-bold text-slate-900 dark:text-white">{ind.pr_number || `IND-${ind.id}`}</td>
                        <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{ind.project_name || 'Active Site'}</td>
                        <td className="py-3 px-3 text-slate-800 dark:text-slate-200">{ind.item_summary || 'Cement & Reinforcement Items'}</td>
                        <td className="py-3 px-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              ind.urgency === 'Urgent'
                                ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                            }`}
                          >
                            {ind.urgency || 'Medium'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-slate-500">{ind.required_date || 'Within 48h'}</td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300">
                            {ind.status || 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 5: APPROVALS ON THE GO ─── */}
        {activeTab === 'approvals' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
              <div className="mb-4">
                <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Approvals on the Go</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Review and approve Purchase Orders, change requests, and contractor bills directly from site.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {approvals.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-2xs space-y-3 flex flex-col justify-between"
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700">
                          {item.type}
                        </span>
                        <span className="text-[11px] text-slate-400">{item.date || 'Pending'}</span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">{item.title}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Vendor: {item.vendor || 'Authorized Supplier'}</p>
                      <div className="pt-1">
                        <span className="text-lg font-black text-slate-900 dark:text-white">
                          <Price amount={item.amount || 50000} />
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                      <button
                        onClick={() => handleApprovalAction(item, 'approve')}
                        className="flex-1 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition-all cursor-pointer flex items-center justify-center gap-1"
                      >
                        <CheckIcon className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => handleApprovalAction(item, 'reject')}
                        className="flex-1 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 6: INSPECTIONS & SNAGS ─── */}
        {activeTab === 'inspections' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Quality & HSE Inspections & Snagging</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Photo-pinned punch lists, safety audits, and defect rectification workflows.</p>
                </div>
                <button
                  onClick={() => setShowSnagModal(true)}
                  className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <PlusIcon className="h-4 w-4" />
                  Report New Snag
                </button>
              </div>

              <div className="space-y-3">
                {inspections.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-2xs space-y-2.5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            item.severity === 'Urgent' || item.severity === 'High'
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                          }`}
                        >
                          {item.severity}
                        </span>
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white">{item.title}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            item.status === 'Resolved'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                              : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                          }`}
                        >
                          {item.status}
                        </span>
                        {item.status !== 'Resolved' && (
                          <button
                            onClick={() => handleResolveSnag(item.id)}
                            className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold cursor-pointer"
                          >
                            Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      <span className="font-semibold text-slate-800 dark:text-slate-200">Location:</span> {item.location} • <span className="font-semibold text-slate-800 dark:text-slate-200">Assigned To:</span> {item.assignedTo}
                    </p>
                    {item.notes && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-[#0d1117] p-2.5 rounded-lg border border-slate-100 dark:border-[#21262d]">
                        {item.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 7: PROJECT DRAWINGS & DOCUMENTS ─── */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs space-y-4">
              <div>
                <h2 className="text-sm font-bold text-[#1B2A41] dark:text-white">Site Blueprints & Drawings</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Access approved architectural blueprints, structural diagrams, and permits.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-slate-200 dark:border-[#30363d] bg-slate-50/50 dark:bg-[#0d1117]/60 space-y-2">
                    <div className="flex items-start justify-between">
                      <DocumentDuplicateIcon className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                      <span className="text-[10px] text-slate-400">PDF Document</span>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white truncate">{doc.title || doc.name || `Blueprint Specification #${idx + 1}`}</h3>
                    <p className="text-[11px] text-slate-500">{doc.category || 'Structural Plan'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ─── MODAL 1: FILE DPR ─── */}
      {showDprModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] rounded-2xl max-w-lg w-full p-5 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#21262d] pb-3">
              <h3 className="text-base font-bold text-[#1B2A41] dark:text-white">File Daily Progress Report (DPR)</h3>
              <button onClick={() => setShowDprModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleDprSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Select Construction Site</label>
                <select
                  value={dprForm.projectId}
                  onChange={(e) => setDprForm({ ...dprForm, projectId: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                >
                  {clientProjects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Work Description / Milestones Executed</label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. 150 sq.m slab casting completed on 4th floor. Curing ongoing for columns C1-C8."
                  value={dprForm.workDescription}
                  onChange={(e) => setDprForm({ ...dprForm, workDescription: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Masons</label>
                  <input
                    type="number"
                    value={dprForm.masons}
                    onChange={(e) => setDprForm({ ...dprForm, masons: e.target.value })}
                    className="w-full text-xs p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Carpenters</label>
                  <input
                    type="number"
                    value={dprForm.carpenters}
                    onChange={(e) => setDprForm({ ...dprForm, carpenters: e.target.value })}
                    className="w-full text-xs p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Helpers</label>
                  <input
                    type="number"
                    value={dprForm.helpers}
                    onChange={(e) => setDprForm({ ...dprForm, helpers: e.target.value })}
                    className="w-full text-xs p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Site Hindrances / Delays (If Any)</label>
                <input
                  type="text"
                  placeholder="e.g. Rain from 2pm to 3:30pm or ReadyMix concrete delay"
                  value={dprForm.hindrances}
                  onChange={(e) => setDprForm({ ...dprForm, hindrances: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                <button
                  type="button"
                  onClick={() => setShowDprModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 shadow-xs cursor-pointer transition-all"
                >
                  Submit DPR
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL 2: RAISE MATERIAL INDENT ─── */}
      {showIndentModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] rounded-2xl max-w-md w-full p-5 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#21262d] pb-3">
              <h3 className="text-base font-bold text-[#1B2A41] dark:text-white">Raise Site Material Indent</h3>
              <button onClick={() => setShowIndentModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleIndentSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Item / Material Required</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 50 Bags Portland Cement or 2 Tons 12mm TMT"
                  value={indentForm.itemName}
                  onChange={(e) => setIndentForm({ ...indentForm, itemName: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Quantity & Unit</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 50 Bags"
                    value={indentForm.quantity}
                    onChange={(e) => setIndentForm({ ...indentForm, quantity: e.target.value })}
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Urgency</label>
                  <select
                    value={indentForm.urgency}
                    onChange={(e) => setIndentForm({ ...indentForm, urgency: e.target.value })}
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  >
                    <option value="Normal">Normal</option>
                    <option value="Medium">Medium</option>
                    <option value="Urgent">Urgent (Site Stoppage)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Required By Date</label>
                <input
                  type="date"
                  value={indentForm.requiredDate}
                  onChange={(e) => setIndentForm({ ...indentForm, requiredDate: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                <button
                  type="button"
                  onClick={() => setShowIndentModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                >
                  Submit Indent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL 3: LOG GATE GRN (DELIVERY RECEIVING) ─── */}
      {showGrnModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] rounded-2xl max-w-md w-full p-5 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#21262d] pb-3">
              <h3 className="text-base font-bold text-[#1B2A41] dark:text-white">Receive Gate GRN</h3>
              <button onClick={() => setShowGrnModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleGrnSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Truck / Vehicle Number</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. MH-04-AB-1234"
                  value={grnForm.vehicleNumber}
                  onChange={(e) => setGrnForm({ ...grnForm, vehicleNumber: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Delivery Challan / Bill Number</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. DC-99482"
                  value={grnForm.challanNumber}
                  onChange={(e) => setGrnForm({ ...grnForm, challanNumber: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Material Received Summary</label>
                <input
                  type="text"
                  placeholder="e.g. 50 Bags Ultratech Cement received in good condition"
                  value={grnForm.materialSummary}
                  onChange={(e) => setGrnForm({ ...grnForm, materialSummary: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                <button
                  type="button"
                  onClick={() => setShowGrnModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                >
                  Verify & Log GRN
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL 4: REPORT SNAG ─── */}
      {showSnagModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#161b22] border border-slate-200 dark:border-[#30363d] rounded-2xl max-w-md w-full p-5 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#21262d] pb-3">
              <h3 className="text-base font-bold text-[#1B2A41] dark:text-white">Report Inspection Snag / Defect</h3>
              <button onClick={() => setShowSnagModal(false)} className="text-slate-400 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSnagSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Defect / Inspection Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Uneven plastering or missing edge protection"
                  value={snagForm.title}
                  onChange={(e) => setSnagForm({ ...snagForm, title: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Location Pin</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Tower B - 4th Floor - Grid C2"
                  value={snagForm.location}
                  onChange={(e) => setSnagForm({ ...snagForm, location: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Severity</label>
                  <select
                    value={snagForm.severity}
                    onChange={(e) => setSnagForm({ ...snagForm, severity: e.target.value })}
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent (Safety Critical)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Assigned Team</label>
                  <input
                    type="text"
                    value={snagForm.assignedTo}
                    onChange={(e) => setSnagForm({ ...snagForm, assignedTo: e.target.value })}
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Corrective Action Notes</label>
                <textarea
                  rows={2}
                  placeholder="Action required to rectify before handover"
                  value={snagForm.notes}
                  onChange={(e) => setSnagForm({ ...snagForm, notes: e.target.value })}
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-[#0d1117] text-slate-900 dark:text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-[#21262d]">
                <button
                  type="button"
                  onClick={() => setShowSnagModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
                >
                  Log Snag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
